/**
 * Stores Reports, after seedtrac STORES' Reports menu (reference screenshots, 2026-10-07): Stock on
 * Hand, Party wise Stock, Stores Item Ledger, Stock Transfer, Captive Consumption, Discard, Reorder
 * Level, SLOC Status, Consumption and Periodical Arrival.
 *
 * One definition per report: its filters and its columns. The API builds the rows and sends the
 * resolved columns with them, so the web table, the Excel sheet and the PDF can never disagree on
 * a heading. Quantities arrive as text already rounded to the item's unit (Kg 3 decimals, Number
 * whole), as the legacy screens print them; nothing is summed across items of different units.
 */
import { STORES_ARRIVALS } from "./stores";
import { STORES_CORRECTIONS } from "./stores-corrections";
import { STORES_OUTBOUND } from "./stores-issue";

/** Every Stores document kind in words: "Arrival – From Vendor", "Good to Damage". */
export function storesKindLabel(kind: string): string {
  if (kind in STORES_ARRIVALS)
    return `Arrival – ${STORES_ARRIVALS[kind as keyof typeof STORES_ARRIVALS].label}`;
  if (kind in STORES_OUTBOUND)
    return STORES_OUTBOUND[kind as keyof typeof STORES_OUTBOUND].title;
  if (kind in STORES_CORRECTIONS)
    return STORES_CORRECTIONS[kind as keyof typeof STORES_CORRECTIONS].label;
  return (
    {
      sloc: "SLOC Updation",
      cycle_count: "Cycle Inventory",
      opening: "Opening Stock",
    }[kind] ?? kind
  );
}

export const STORES_REPORT_KEYS = [
  "stock-on-hand",
  "party-wise",
  "item-ledger",
  "stock-transfer",
  "captive",
  "discard",
  "reorder-level",
  "sloc-status",
  "consumption",
  "periodical-arrival",
] as const;
export type StoresReportKey = (typeof STORES_REPORT_KEYS)[number];

export const isStoresReportKey = (k: string): k is StoresReportKey =>
  (STORES_REPORT_KEYS as readonly string[]).includes(k);

/**
 * `asOn` a single date (stock as it stood at the end of that day); `period` From/To; `withSloc` the
 * Stock on Hand "With SLOC" tick; `party` a vendor, plant or Stores party; `partyText` Captive's
 * "OR Specify"; `direction` Stock Transfer out of or into this plant; `sloc` a warehouse, bin or
 * sub-bin.
 */
export type StoresReportFilter =
  | "asOn"
  | "period"
  | "classification"
  | "item"
  | "condition"
  | "withSloc"
  | "party"
  | "partyText"
  | "direction"
  | "sloc";

export type StoresReportFormat = "text" | "doc" | "int" | "num" | "date";

export interface StoresReportColumn {
  key: string;
  label: string;
  /** A header above a run of columns ("Total", "Good", "Issue"). */
  group?: string;
  format: StoresReportFormat;
}

export interface StoresReportDef {
  key: StoresReportKey;
  /** The legacy menu's words. */
  menu: string;
  title: string;
  hint: string;
  filters: readonly StoresReportFilter[];
  /** Filters the report cannot run without. */
  required: readonly StoresReportFilter[];
  permission: StoresReportPermission;
  /**
   * Also open to Stores View: SLOC Status took over the Utility's SLOC Search and Stores Item Search,
   * and Reorder Level the Order Placement screen, which every Stores user had (user, 2026-10-30).
   */
  openTo?: "stores.view";
}

/** The report's own code opens it, or Stores View where the report took over a Stores screen. */
export const canOpenStoresReport = (
  def: StoresReportDef,
  can: (permission: StoresReportPermission | "stores.view") => boolean
): boolean => can(def.permission) || (!!def.openTo && can(def.openTo));

export type StoresReportPermission =
  | "stores.reports.stock_on_hand"
  | "stores.reports.party_wise"
  | "stores.reports.item_ledger"
  | "stores.reports.stock_transfer"
  | "stores.reports.captive"
  | "stores.reports.discard"
  | "stores.reports.reorder_level"
  | "stores.reports.sloc_status"
  | "stores.reports.consumption"
  | "stores.reports.periodical_arrival";

export const STORES_REPORTS: Record<StoresReportKey, StoresReportDef> = {
  "stock-on-hand": {
    key: "stock-on-hand",
    menu: "Stock on Hand Report",
    title: "Stock on Hand Report",
    hint: "Good or Damage stock per item as on a date, optionally bin by bin.",
    filters: ["asOn", "classification", "item", "withSloc", "condition"],
    required: ["asOn", "condition"],
    permission: "stores.reports.stock_on_hand",
  },
  "party-wise": {
    key: "party-wise",
    menu: "Party wise Stock Report",
    title: "Party wise Stock Report",
    hint: "What came in from and went out to one party over a period.",
    filters: ["period", "party", "classification", "item"],
    required: ["party"],
    permission: "stores.reports.party_wise",
  },
  "item-ledger": {
    key: "item-ledger",
    menu: "Stores Item Ledger Report",
    title: "Stores Item Ledger Report",
    hint: "Every receipt and issue of one item, with the running balance.",
    filters: ["period", "classification", "item", "condition"],
    required: ["item", "condition"],
    permission: "stores.reports.item_ledger",
  },
  "stock-transfer": {
    key: "stock-transfer",
    menu: "Stock Transfer Report",
    title: "Stock Transfer Report",
    hint: "Stock Transfers sent to or received from other plants.",
    filters: ["period", "direction", "classification", "item"],
    required: ["direction"],
    permission: "stores.reports.stock_transfer",
  },
  captive: {
    key: "captive",
    menu: "Captive Consumption Report",
    title: "Captive Consumption Report",
    hint: "Captive Consumption by party over a period.",
    filters: ["period", "party", "partyText"],
    required: [],
    permission: "stores.reports.captive",
  },
  discard: {
    key: "discard",
    menu: "Discard Report",
    title: "Discard Report",
    hint: "Material Discards over a period.",
    filters: ["period", "classification", "item"],
    required: [],
    permission: "stores.reports.discard",
  },
  "reorder-level": {
    key: "reorder-level",
    menu: "Reorder Level Report",
    title: "Reorder Level as on Date Report",
    hint: "Items with a re-order level, their Good stock and last order; R where it is at or below. Order Placement is done from here.",
    filters: ["asOn", "classification"],
    required: ["asOn"],
    permission: "stores.reports.reorder_level",
    openTo: "stores.view",
  },
  "sloc-status": {
    key: "sloc-status",
    menu: "SLOC Status Report",
    title: "SLOC Status Report",
    hint: "What sits in a warehouse, bin or sub-bin now, or every bin an item sits in.",
    // A SLOC or an item (or both): the old Utility's SLOC Search and Stores Item Search.
    filters: ["sloc", "classification", "item"],
    required: [],
    permission: "stores.reports.sloc_status",
    openTo: "stores.view",
  },
  consumption: {
    key: "consumption",
    menu: "Consumption Report",
    title: "Consumption Report",
    hint: "Issued to the floor, less what came back: what was used, per item.",
    filters: ["period", "classification", "item"],
    required: [],
    permission: "stores.reports.consumption",
  },
  "periodical-arrival": {
    key: "periodical-arrival",
    menu: "Periodical Arrival Report",
    title: "Periodical Arrival Report",
    hint: "Every vendor arrival (GRN) line over a period.",
    filters: ["period"],
    required: [],
    permission: "stores.reports.periodical_arrival",
  },
};

export const storesReport = (k: StoresReportKey) => STORES_REPORTS[k];

const c = (
  key: string,
  label: string,
  format: StoresReportFormat,
  group?: string
): StoresReportColumn =>
  group ? { key, label, format, group } : { key, label, format };

const SERIAL = c("serial", "#", "int");
const DATE = c("date", "Date", "date");
const DOC = c("docNo", "Transaction Id", "doc");
const CLASS = c("classification", "Classification", "text");
const ITEM = c("item", "Item", "text");
const UOM = c("uom", "UoM", "text");
const pair = (prefix: string, group: string, qtyLabel = "Qty") => [
  c(`${prefix}Ups`, "UPS", "int", group),
  c(`${prefix}Qty`, qtyLabel, "num", group),
];

/** The columns a report prints, given its filters (Stock on Hand "With SLOC", transfer direction). */
export function storesReportColumns(
  key: StoresReportKey,
  f: {
    withSloc?: boolean | undefined;
    direction?: "out" | "in" | undefined;
  } = {}
): StoresReportColumn[] {
  switch (key) {
    case "stock-on-hand":
      return [
        SERIAL,
        CLASS,
        ITEM,
        UOM,
        ...(f.withSloc ? [c("sloc", "SLOC", "doc")] : []),
        ...pair("total", "Total", "QTY"),
        c("status", "Status", "text"),
      ];
    case "party-wise":
      return [
        SERIAL,
        DATE,
        DOC,
        c("transaction", "Transaction", "text"),
        CLASS,
        ITEM,
        UOM,
        ...pair("in", "Received"),
        ...pair("out", "Sent"),
      ];
    case "item-ledger":
      return [
        DATE,
        DOC,
        c("particulars", "Particulars", "text"),
        ...pair("in", "Receipt"),
        ...pair("out", "Issue"),
        ...pair("bal", "Balance"),
      ];
    case "stock-transfer":
      return f.direction === "in"
        ? [
            SERIAL,
            DATE,
            DOC,
            c("plant", "From Plant", "text"),
            c("stnNo", "STN No.", "doc"),
            CLASS,
            ITEM,
            UOM,
            ...pair("good", "Good"),
            ...pair("damage", "Damage"),
          ]
        : [
            SERIAL,
            DATE,
            c("docNo", "STN No.", "doc"),
            c("plant", "To Plant", "text"),
            CLASS,
            ITEM,
            UOM,
            c("ups", "UPS", "int"),
            c("qty", "Qty", "num"),
            c("gatePass", "Gate Pass", "doc"),
            c("received", "Received As", "doc"),
          ];
    case "captive":
      return [
        SERIAL,
        DATE,
        DOC,
        c("party", "Party", "text"),
        CLASS,
        ITEM,
        UOM,
        c("ups", "UPS", "int"),
        c("qty", "Qty", "num"),
        c("returnStatus", "Return Status", "text"),
      ];
    case "discard":
      return [
        SERIAL,
        DATE,
        DOC,
        c("refNo", "Discard Inst. Ref. No.", "text"),
        c("party", "Party", "text"),
        CLASS,
        ITEM,
        UOM,
        c("ups", "UPS", "int"),
        c("qty", "Qty", "num"),
      ];
    case "reorder-level":
      return [
        SERIAL,
        CLASS,
        ITEM,
        UOM,
        c("level", "Reorder Level", "num"),
        c("ups", "UPS", "int"),
        c("qty", "Quantity", "num"),
        c("lastOrdered", "Last Ordered", "date"),
        c("remarks", "Remarks", "text"),
      ];
    case "sloc-status":
      return [
        SERIAL,
        c("wh", "WH", "doc"),
        c("bin", "Bin", "doc"),
        c("subbin", "Subbin", "doc"),
        CLASS,
        ITEM,
        UOM,
        c("condition", "G/D", "text", "SLOC"),
        c("ups", "UPS", "int", "SLOC"),
        c("qty", "Qty", "num", "SLOC"),
      ];
    case "consumption":
      return [
        SERIAL,
        CLASS,
        ITEM,
        UOM,
        ...pair("issue", "Issue", "QTY"),
        ...pair("ret", "Internal Return", "QTY"),
        ...pair("used", "Used Qty", "QTY"),
      ];
    case "periodical-arrival":
      return [
        DATE,
        c("docNo", "GRN", "doc"),
        c("dcNo", "DC / Invoice No.", "text"),
        c("poRef", "PO Ref No.", "text"),
        c("vendor", "Vendor Name", "text"),
        CLASS,
        ITEM,
        UOM,
        ...pair("dc", "As per DC"),
        ...pair("good", "Good"),
        ...pair("damage", "Damage"),
        ...pair("ex", "Ex/SH"),
        c("mode", "Mode", "text", "Transport Details"),
        c("transport", "Name", "text", "Transport Details"),
        c("sloc", "SLOC", "doc"),
      ];
  }
}

export type StoresReportCell = string | number | null;
export type StoresReportRow = Record<string, StoresReportCell> & {
  /** A row the table sets apart: the ledger's opening and closing lines. */
  _emphasis?: StoresReportCell;
};

export interface StoresReportResult {
  key: StoresReportKey;
  title: string;
  plant: string;
  /** The lines under the title: "As on Date: 07/10/2026", "Classification: ALL". */
  heading: string[];
  columns: StoresReportColumn[];
  rows: StoresReportRow[];
  rowCount: number;
}

/** How many rows a report may hold before it asks for narrower filters. */
export const STORES_REPORT_ROW_CAP = 5000;

/** A party in the Party wise / Captive pickers: a vendor, a plant or a Stores party. */
export type StoresReportPartyKind = "vendor" | "plant" | "party";
export const storesPartyKey = (kind: StoresReportPartyKind, id: number) =>
  `${kind}:${id}`;
export function parseStoresPartyKey(
  k: string
): { kind: StoresReportPartyKind; id: number } | null {
  const m = /^(vendor|plant|party):(\d+)$/.exec(k);
  return m ? { kind: m[1] as StoresReportPartyKind, id: Number(m[2]) } : null;
}

/** A cell as text, for the Excel and PDF outputs. */
export const storesReportCellText = (v: StoresReportCell | undefined) =>
  v === null || v === undefined ? "" : String(v);
